import { useMemo, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import { useTranslation } from 'react-i18next'
import { LayoutList } from 'lucide-react'

import { SearchSelect } from '@/components/SearchSelect'
import { useNetwork } from '@/contexts/NetworkContext'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { LineItemsApi } from '@/services/lineItems/lineItemsApi'

export interface LineItemSelectProps {
  /** Line item id, or '' for no selection. */
  value: string
  onChange: (lineItemId: string) => void
  /** Label for the empty choice, offered first to clear the selection. */
  anyLabel?: string
  disabled?: boolean
  className?: string
}

/**
 * Line item picker that searches the server as the user types — a network
 * has too many line items to list whole. With `anyLabel` it serves as a list
 * filter, where '' means "not filtered".
 */
export function LineItemSelect({ anyLabel, ...props }: LineItemSelectProps) {
  const { t } = useTranslation()
  const { scopeKey } = useNetwork()
  const [term, setTerm] = useState('')
  const search = useDebouncedValue(term)

  const { data: lineItems = [], isLoading, isFetching } = useQuery({
    queryKey: ['lineitems', 'picker', scopeKey, search],
    queryFn: async () => (await LineItemsApi.getPage(1, 50, { objectState: 'all', search })).results,
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
  })
  // The picked line item may not be among the matches — fetch it on its own to show its name.
  const listHasValue = !props.value || lineItems.some((li) => li.id === props.value)
  const { data: picked } = useQuery({
    queryKey: ['lineitem', 'name', props.value],
    queryFn: () => LineItemsApi.getById(props.value),
    enabled: !isLoading && !listHasValue,
    staleTime: 5 * 60 * 1000,
  })
  const options = useMemo(
    () => lineItems.map((li) => ({ value: li.id, label: li.name })),
    [lineItems]
  )

  return (
    <SearchSelect
      {...props}
      options={options}
      placeholder={anyLabel ?? t('lineItems.selectLineItem')}
      fallbackLabel={picked?.name}
      clearable={!!anyLabel}
      icon={LayoutList}
      loading={isLoading}
      onSearchChange={setTerm}
      searching={isFetching && !isLoading}
      searchPlaceholder={t('lineItems.searchLineItems')}
      emptyText={t('lineItems.noLineItemsFound')}
    />
  )
}
